import { selectFeatureFlags, selectIsBossOrSalesBossLevel } from 'core/userContext';
import { BasicSelectField, InputField } from 'lib/iterable-hook-form';
import {
  SMS_FIELD_NAMES,
  SHORTLINKS_MAX_VALUE,
  SHORTLINKS_MIN_VALUE,
  smsOptOutOptions,
  SmsOptOutValues,
  SMS_HEADING_TEXT_WITH_WHATSAPP,
  SMS_HEADING_TEXT,
} from 'modules/projectSettings/constants';
import { useProjectSettingsQueryContext } from 'modules/projectSettings/contexts';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_VARIANTS } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { FormField } from 'ui/formField';
import { LongArrowRight } from 'ui/icons';
import { LinkButton } from 'ui/linkButton';
import { Body2, colors } from 'ui/theme';
import { ConnectedFormField, MarkdownDescription, OptionalLabelText } from './ConnectedFormField';
import { FormSection } from './FormSection';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags/useWhatsAppMessageChannelFeatureFlag';

const {
  SMS_OPT_OUT_INSTRUCTIONS,
  SMS_DISCLAIMER,
  SMS_SHORTLINKS_MIN_LENGTH_MARKETING,
  SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL,
} = SMS_FIELD_NAMES;

const StyledSelectField = styled(BasicSelectField)`
  width: 100%;
`;

const StyledSMSShortLinkSuffix = styled(Body2)`
  color: ${colors.textSecondary};
`;

const SMS_SHORTLINKS_DEFAULT_MIN_LENGTH = 5;

export const SMS_SECTION_TEXT = {
  /** SMS Section */
  sectionHeadingText: SMS_HEADING_TEXT,
  sectionHeadingTextWithWhatsApp: SMS_HEADING_TEXT_WITH_WHATSAPP,
  sectionId: 'sms',
  /** manage global settings */
  settingsButtonText: 'Manage',
  settingsDescriptionText: 'Customize opt-out text and message frequency by country.',
  settingsLabelText: 'Global SMS Settings',
  settingsUrl: '/settings/project/globalSms',
  /** opt-out instructions */
  optOutInstructionsDescriptionText:
    'Some carriers may filter SMS messages that omit opt-out instructions, so it’s best to include them in every campaign.',
  optOutInstructionsDescriptionTextId: 'optOutInstructionsDescriptionText',
  optOutInstructionsLabelText: 'Include SMS opt-out instructions',
  optOutInstructionsSupportUrl:
    'https://support.iterable.com/hc/en-us/articles/211970843#sms-opt-out-instructions',
  optOutInstructionsId: 'smsOptOutInstructionsOptions',
  /** SMS disclaimer */
  smsDisclaimerLabelText: 'SMS opt-out instructions text',
  smsDisclaimerDescriptionText:
    'Customize the opt-out instructions included with the first SMS campaigns you send.',
  smsDisclaimerPlaceholderText: 'Msg & data rates may apply. Text ‘STOP’ to quit.',
  smsDisclaimerId: 'smsOptOutInput',
  /** SMS short links */
  smsShortLinkDescriptionText:
    'Set a minimum character length for unique keys in SMS shortlinks. Total length includes your link domain plus a unique key. Unique keys may be up to 6 characters longer than the minimum.',
  smsShortLinkDescriptionTextId: 'smsShortLinkDescriptionText',
  smsShortLinkLabelText: 'SMS shortlink character preference',
  smsWhatsappShortLinkLabelText: 'Shortlink character preference for SMS & WhatsApp',
  smsShortLinkSupportUrl: 'https://support.iterable.com/hc/articles/4403865896724',
  smsShortLinkIdPrefix: 'smsShortLinks',
  smsShortLinksInputSuffix: 'characters',
  smsShortLinksMarketingSuffix: 'for Marketing channels',
  smsShortLinksTransactionalSuffix: 'for Transactional channels',
};

export const SmsFormSection = () => {
  const { hasSmsChannel } = useProjectSettingsQueryContext();
  const optOutInstructionsValue = useWatch({ name: SMS_OPT_OUT_INSTRUCTIONS });
  const { GlobalSmsFirstPartyUI } = useSelector(selectFeatureFlags);
  const isBossOrSalesBossLevel = useSelector(selectIsBossOrSalesBossLevel);
  const smsShortLinkMarketingMinLength = useWatch({ name: SMS_SHORTLINKS_MIN_LENGTH_MARKETING });
  const smsShortLinkTransactionalMinLength = useWatch({
    name: SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL,
  });
  const { isWhatsAppEnabled } = useWhatsAppMessageChannelFeature();

  if (!hasSmsChannel) return null;

  return (
    <FormSection
      id={SMS_SECTION_TEXT.sectionId}
      data-test={`${SMS_SECTION_TEXT.sectionId}-form-section`}
      headingText={
        isWhatsAppEnabled
          ? SMS_SECTION_TEXT.sectionHeadingTextWithWhatsApp
          : SMS_SECTION_TEXT.sectionHeadingText
      }
    >
      {GlobalSmsFirstPartyUI ? (
        <FormField
          labelText={SMS_SECTION_TEXT.settingsLabelText}
          descriptionText={SMS_SECTION_TEXT.settingsDescriptionText}
        >
          <LinkButton variant={BUTTON_VARIANTS.PRIMARY} to={SMS_SECTION_TEXT.settingsUrl}>
            <span>{SMS_SECTION_TEXT.settingsButtonText}</span>
            <LongArrowRight />
          </LinkButton>
        </FormField>
      ) : (
        <>
          <ConnectedFormField
            labelText={SMS_SECTION_TEXT.optOutInstructionsLabelText}
            descriptionText={
              <MarkdownDescription
                id={SMS_SECTION_TEXT.optOutInstructionsDescriptionTextId}
                markdownText={SMS_SECTION_TEXT.optOutInstructionsDescriptionText}
                supportUrl={SMS_SECTION_TEXT.optOutInstructionsSupportUrl}
              />
            }
            name={SMS_OPT_OUT_INSTRUCTIONS}
          >
            <StyledSelectField
              name={SMS_OPT_OUT_INSTRUCTIONS}
              id={SMS_OPT_OUT_INSTRUCTIONS}
              options={smsOptOutOptions(isBossOrSalesBossLevel)}
              data-test={SMS_SECTION_TEXT.optOutInstructionsId}
            />
          </ConnectedFormField>
          {optOutInstructionsValue !== SmsOptOutValues.Never && (
            <ConnectedFormField
              labelText={<OptionalLabelText text={SMS_SECTION_TEXT.smsDisclaimerLabelText} />}
              descriptionText={SMS_SECTION_TEXT.smsDisclaimerDescriptionText}
              name={SMS_DISCLAIMER}
            >
              <InputField
                data-test={SMS_SECTION_TEXT.smsDisclaimerId}
                name={SMS_DISCLAIMER}
                id={SMS_DISCLAIMER}
                placeholder={SMS_SECTION_TEXT.smsDisclaimerPlaceholderText}
              />
            </ConnectedFormField>
          )}
        </>
      )}
      <ConnectedFormField
        labelText={
          <OptionalLabelText
            text={
              isWhatsAppEnabled
                ? SMS_SECTION_TEXT.smsWhatsappShortLinkLabelText
                : SMS_SECTION_TEXT.smsShortLinkLabelText
            }
          />
        }
        descriptionText={
          <MarkdownDescription
            id={SMS_SECTION_TEXT.smsShortLinkDescriptionTextId}
            markdownText={SMS_SECTION_TEXT.smsShortLinkDescriptionText}
            supportUrl={SMS_SECTION_TEXT.smsShortLinkSupportUrl}
          />
        }
        name={SMS_SHORTLINKS_MIN_LENGTH_MARKETING}
      >
        <FlexSpacer>
          <InputField
            data-test={`${SMS_SECTION_TEXT.smsShortLinkIdPrefix}-marketing`}
            name={SMS_SHORTLINKS_MIN_LENGTH_MARKETING}
            id={SMS_SHORTLINKS_MIN_LENGTH_MARKETING}
            Suffix={SMS_SECTION_TEXT.smsShortLinksInputSuffix}
            width="253px"
            defaultValue={smsShortLinkMarketingMinLength || SMS_SHORTLINKS_DEFAULT_MIN_LENGTH}
            type="number"
            valueAsNumber
            minValue={SHORTLINKS_MIN_VALUE}
            maxValue={SHORTLINKS_MAX_VALUE}
          />
          <StyledSMSShortLinkSuffix>
            {SMS_SECTION_TEXT.smsShortLinksMarketingSuffix}
          </StyledSMSShortLinkSuffix>
        </FlexSpacer>
      </ConnectedFormField>
      <ConnectedFormField name={SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL}>
        <FlexSpacer>
          <InputField
            data-test={`${SMS_SECTION_TEXT.smsShortLinkIdPrefix}-transactional`}
            name={SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL}
            id={SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL}
            defaultValue={smsShortLinkTransactionalMinLength || SMS_SHORTLINKS_DEFAULT_MIN_LENGTH}
            Suffix={SMS_SECTION_TEXT.smsShortLinksInputSuffix}
            width="253px"
            type="number"
            valueAsNumber
            minValue={SHORTLINKS_MIN_VALUE}
            maxValue={SHORTLINKS_MAX_VALUE}
          />
          <StyledSMSShortLinkSuffix>
            {SMS_SECTION_TEXT.smsShortLinksTransactionalSuffix}
          </StyledSMSShortLinkSuffix>
        </FlexSpacer>
      </ConnectedFormField>
    </FormSection>
  );
};
